import { Camera, FileText, ImagePlus, type LucideIcon } from 'lucide-react';
import { useEffect, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';

export type AttachmentSource = 'camera' | 'gallery' | 'document';

export type AttachmentMenuProps = {
  open: boolean;
  onClose: () => void;
  onSelect: (source: AttachmentSource) => void;
};

/**
 * Bottom sheet com 3 opcoes de anexo (Camera, Galeria, Documento), estilo
 * WhatsApp/Telegram. Cada opcao dispara um <input type="file"> diferente
 * (com `capture="environment"` na camera) no parent -- este componente
 * apenas roteia a escolha via onSelect.
 *
 * Mobile: full-width slide-in da base; desktop: centralizado com max-w.
 * Esc fecha; tap no backdrop fecha; body overflow travado enquanto aberto.
 */
export const AttachmentMenu = ({ open, onClose, onSelect }: AttachmentMenuProps) => {
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  const handleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Escolher origem do anexo"
      className="fixed inset-0 z-50 flex flex-col justify-end bg-black/40 animate-in fade-in duration-150 md:items-center md:justify-center md:px-4"
      onClick={onClose}
      onKeyDown={handleKey}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full rounded-t-3xl bg-white pb-[max(20px,env(safe-area-inset-bottom))] shadow-2xl animate-in slide-in-from-bottom-4 duration-200 md:w-full md:max-w-sm md:rounded-3xl md:pb-2"
      >
        <div className="flex justify-center pt-3 pb-1 md:hidden">
          <div className="h-1 w-10 rounded-full bg-gray-300" />
        </div>

        <div className="px-2 py-2">
          <MenuItem icon={Camera} label="Câmera" onClick={() => onSelect('camera')} />
          <MenuItem icon={ImagePlus} label="Galeria" onClick={() => onSelect('gallery')} />
          <MenuItem icon={FileText} label="Documento" onClick={() => onSelect('document')} />
        </div>

        <div className="px-4 pt-1 pb-2 md:hidden">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-xl py-3 text-center text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

type MenuItemProps = {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
};

const MenuItem = ({ icon: Icon, label, onClick }: MenuItemProps) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-center gap-4 rounded-xl px-4 py-3 text-left transition-colors hover:bg-gray-50 active:bg-gray-100"
  >
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-700">
      <Icon className="h-5 w-5" />
    </div>
    <span className="text-base font-medium text-gray-900">{label}</span>
  </button>
);
